import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { AlertTriangle, RefreshCw, HelpCircle } from 'lucide-react';

const ErrorPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as { traceId?: string; message?: string } | null) || {};
  const searchParams = new URLSearchParams(location.search);
  const traceId = state.traceId || searchParams.get('traceId') || undefined;
  const messageOverride = state.message || searchParams.get('message') || undefined;

  // If the user performed a browser reload while on this error page,
  // redirect once to `/` so the app root logic can attempt recovery.
  useEffect(() => {
    try {
      const navEntries = (performance as any).getEntriesByType?.('navigation') || [];
      const nav = navEntries[0];
      const wasReload = nav ? nav.type === 'reload' : (performance as any).navigation?.type === 1;

      if (wasReload && !sessionStorage.getItem('errorRefreshRedirected')) {
        // mark so we don't loop
        sessionStorage.setItem('errorRefreshRedirected', '1');
        // preserve traceId/message in state when redirecting so we can show it if needed
        navigate('/', { replace: true, state: { traceId, message: messageOverride } });
      }
    } catch (e) {
      // ignore - don't block rendering
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12">
      <div className="w-full max-w-4xl bg-white shadow-xl rounded-2xl border border-slate-200 p-8 md:p-12">
        <div className="flex flex-col gap-6 md:gap-8">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 flex items-center justify-center rounded-full bg-orange-100 text-orange-600">
              <AlertTriangle className="h-7 w-7" />
            </div>
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-slate-500 font-semibold">Unexpected error</p>
              <h1 className="mt-2 text-3xl md:text-4xl font-semibold text-slate-900">A glitch snuck in. We’re on it.</h1>
              <p className="mt-3 text-base md:text-lg text-slate-600 max-w-3xl">
                {messageOverride || 'Something went wrong on our side. The good news: your data should be safe. Try the steps below or head back to a safe page.'}
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:gap-5 md:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-800">1. Refresh safely</p>
              <p className="text-sm text-slate-600 mt-2">Reload this page to retry the last action.</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-800">2. Jump to dashboard</p>
              <p className="text-sm text-slate-600 mt-2">Return to your dashboard and re-open the feature.</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-800">3. Check status</p>
              <p className="text-sm text-slate-600 mt-2">If this repeats, check your service status or updates.</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-800">4. Clear and retry</p>
              <p className="text-sm text-slate-600 mt-2">Close extra tabs, then try again. Heavy pages can stall.</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-800">5. Capture the moment</p>
              <p className="text-sm text-slate-600 mt-2">Note what you were doing. Screenshots help us fix it faster.</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-800">6. Contact support</p>
              <p className="text-sm text-slate-600 mt-2">Share the steps above with support so we can investigate.</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="text-sm text-slate-600">
              Need help right now? Email <span className="font-semibold text-slate-800">support@yourdomain.com</span> with a brief description and any screenshots.
              {traceId ? (
                <span className="block text-xs text-slate-500 mt-1">Reference ID: {traceId}</span>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate('/', { replace: true })}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-900"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
              <button
                type="button"
                onClick={() => navigate('/support', { state: { traceId, message: messageOverride } })}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-300"
              >
                <HelpCircle className="h-4 w-4" />
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorPage;